import React, { useState } from 'react';
import styled from 'styled-components';
import { chatApi, type ServiceTitanEquipmentReviewArtifact } from '../../services/chatApi';


const Card = styled.section`
  width: min(100%, 560px);
  margin-top: 14px;
  padding: 16px;
  border: 1px solid ${props => props.theme?.token?.colorBorder ?? '#d8dee8'};
  border-radius: 14px;
  background: ${props => props.theme?.token?.colorBgContainer ?? '#fff'};
  color: ${props => props.theme?.token?.colorText ?? '#172033'};
  box-shadow: 0 8px 24px rgba(23, 32, 51, 0.08);
`;

const Eyebrow = styled.div`
  margin-bottom: 5px;
  color: ${props => props.theme?.token?.colorPrimary ?? '#2672be'};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const Title = styled.h3`
  margin: 0 0 4px;
  font-size: 18px;
`;

const Address = styled.p`
  margin: 0 0 14px;
  color: ${props => props.theme?.token?.colorTextSecondary ?? '#596579'};
  font-size: 14px;
`;

const Details = styled.dl`
  display: grid;
  grid-template-columns: minmax(92px, auto) 1fr;
  gap: 7px 14px;
  margin: 0;
  font-size: 14px;

  dt { color: ${props => props.theme?.token?.colorTextSecondary ?? '#667085'}; }
  dd { margin: 0; font-weight: 600; overflow-wrap: anywhere; }
`;

const Notice = styled.div<{ $tone?: 'warning' | 'success' }>`
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${props => props.$tone === 'success'
    ? 'rgba(31, 157, 85, 0.10)'
    : 'rgba(210, 132, 21, 0.11)'};
  color: ${props => props.$tone === 'success' ? '#147a40' : '#875407'};
  font-size: 13px;
  line-height: 1.4;
`;

const Confirmation = styled.code`
  display: block;
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${props => props.theme?.token?.colorFillTertiary ?? '#f3f5f8'};
  font-size: 12px;
  white-space: normal;
  overflow-wrap: anywhere;
`;

const ConfirmButton = styled.button`
  width: 100%;
  margin-top: 12px;
  padding: 12px 16px;
  border: 0;
  border-radius: 10px;
  background: ${props => props.theme?.token?.colorPrimary ?? '#2672be'};
  color: #fff;
  cursor: pointer;
  font: inherit;
  font-weight: 700;

  &:disabled { cursor: not-allowed; opacity: 0.55; }
`;

const ErrorText = styled.p`
  margin: 10px 0 0;
  color: ${props => props.theme?.token?.colorError ?? '#b42318'};
  font-size: 13px;
`;

function serviceAddress(location?: Record<string, any>): string {
  const address = location?.serviceLocationAddress || location?.address || {};
  const street = [address.street, address.unit].filter(Boolean).join(' ');
  const cityLine = [address.city, address.state, address.zip].filter(Boolean).join(', ');
  return [location?.name, street, cityLine].filter(Boolean).join(' · ') || 'Verified job location';
}

function value(value: unknown): string {
  return String(value ?? '').trim() || 'Not provided';
}

const usCalendarDate = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

function displayDate(input: unknown): string {
  const raw = String(input ?? '').trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/.exec(raw);
  if (!match) return value(input);
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year
    || parsed.getUTCMonth() !== month - 1
    || parsed.getUTCDate() !== day
  ) {
    return raw;
  }
  return usCalendarDate.format(parsed);
}

interface Props {
  sessionId: string;
  artifact: ServiceTitanEquipmentReviewArtifact;
  onUpdated: (artifact: ServiceTitanEquipmentReviewArtifact) => void;
}

const ServiceTitanEquipmentReviewCard: React.FC<Props> = ({
  sessionId,
  artifact,
  onUpdated,
}) => {
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState('');
  const equipment = artifact.equipment_draft || {};
  const customFields = Array.isArray(equipment.customFields) ? equipment.customFields : [];
  const exactMatches = artifact.duplicate_check?.exactMatches || [];
  const potentialMatches = artifact.duplicate_check?.potentialMatches || [];
  const created = artifact.status === 'created';
  const invalidated = artifact.status === 'invalidated';
  const duplicate = artifact.status === 'duplicate' || exactMatches.length > 0;
  const expired = Boolean(
    artifact.expires_at
    && Number.isFinite(Date.parse(artifact.expires_at))
    && Date.parse(artifact.expires_at) <= Date.now(),
  );

  const commit = async () => {
    setCommitting(true);
    setError('');
    try {
      onUpdated(await chatApi.commitServiceTitanEquipment(sessionId, artifact));
    } catch (requestError: any) {
      const invalidatedArtifact = requestError?.response?.data?.data;
      if (invalidatedArtifact?.kind === 'servicetitan_equipment_review') {
        onUpdated(invalidatedArtifact as ServiceTitanEquipmentReviewArtifact);
      }
      setError(
        requestError?.response?.data?.message
        || requestError?.message
        || 'Unable to add this equipment. Prepare a fresh review and try again.',
      );
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Card aria-label="ServiceTitan equipment review">
      <Eyebrow>ServiceTitan equipment review</Eyebrow>
      <Title>{value(equipment.name)}</Title>
      <Address>{serviceAddress(artifact.location)}</Address>
      <Details>
        <dt>Type</dt><dd>{value(equipment.equipmentType?.name)}</dd>
        <dt>Manufacturer</dt><dd>{value(equipment.manufacturer)}</dd>
        <dt>Model</dt><dd>{value(equipment.model)}</dd>
        <dt>Serial</dt><dd>{value(equipment.serialNumber)}</dd>
        {equipment.barcodeId && <><dt>Barcode</dt><dd>{value(equipment.barcodeId)}</dd></>}
        <dt>Installed</dt><dd>{displayDate(artifact.installation_date?.date || equipment.installedOn)}</dd>
        <dt>Date source</dt><dd>{value(artifact.installation_date?.source_label)}</dd>
        {equipment.manufacturedOn && <><dt>Manufactured</dt><dd>{displayDate(equipment.manufacturedOn)}</dd></>}
        {customFields.map((field: Record<string, any>, index: number) => (
          <React.Fragment key={`${field.name || 'field'}-${index}`}>
            <dt>{value(field.name || `Custom field ${index + 1}`)}</dt>
            <dd>{value(field.value)}</dd>
          </React.Fragment>
        ))}
        <dt>Photos</dt><dd>{artifact.source_photos?.length || 0}</dd>
        <dt>Job</dt><dd>{value(artifact.job_display_label || artifact.job?.jobNumber)}</dd>
      </Details>

      {potentialMatches.length > 0 && !duplicate && (
        <Notice>{potentialMatches.length} possible duplicate{potentialMatches.length === 1 ? '' : 's'} found. Review before adding.</Notice>
      )}
      {duplicate && (
        <Notice>An equipment record with this serial number or barcode already exists at this location. Creation is blocked.</Notice>
      )}
      {expired && !created && !duplicate && !invalidated && (
        <Notice>This review expired. Ask SmartDocs to prepare a fresh duplicate check before adding equipment.</Notice>
      )}
      {invalidated && (
        <Notice>
          This review is no longer valid. {artifact.invalidation_reason || 'Provide the correct installation date and prepare a new review.'}
        </Notice>
      )}
      {created && (
        <Notice $tone="success">
          Added to ServiceTitan and read back successfully.
        </Notice>
      )}

      {!created && !duplicate && !invalidated && (
        <>
          <Confirmation>{artifact.confirmation_text}</Confirmation>
          <ConfirmButton
            type="button"
            disabled={committing || expired || !artifact.can_commit}
            onClick={commit}
          >
            {committing ? 'Adding equipment…' : (expired ? 'Review expired' : 'Confirm and add equipment')}
          </ConfirmButton>
        </>
      )}
      {error && <ErrorText role="alert">{error}</ErrorText>}
    </Card>
  );
};

export default ServiceTitanEquipmentReviewCard;
